<template>
  <div
    class="tw-flex tw-flex-col tw-rounded-sm tw-p-5 tw-bg-primary-grey-50 tw-h-full"
    :data-test-id="`${tier}-${dataTestIdType}-card`"
  >
    <div class="tw-flex tw-gap-2 tw-items-center tw-mb-2">
      <div
        v-tippy="{
          content: tooltipText,
          placement: 'top',
        }"
        class="tw-w-6 tw-h-6 tw-p-0.5 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-place-self-center"
      >
        <component :is="icon" width="16" height="16" />
      </div>
      <div class="tw-font-semibold tw-text-lg">{{ $t(title) }}</div>
    </div>
    <div class="tw-flex-1">{{ $t(text) }}</div>
    <div class="tw-flex tw-w-full tw-justify-end">
      <t-link
        class="tw-text-primary-grey-700"
        :is-external="true"
        :href="documentationLink"
        :data-test-id="`${tier}-${dataTestIdType}-card-learn-more-external-link`"
        :string="t('learn-more')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks";
import { SERVICE_ICON } from "@/utils/services";
import { useIsMspRoute } from "@/composables/useIsMspRoute.ts";
import { CUSTOMER, MSP } from "@/constants";
import TLink from "@/components/TLink";

type EmptyCard =
  "EMAIL_SECURITY" | "PHISHING_SIMULATION" | "SAT" | "EMAIL_ARCHIVING";

const props = defineProps<{ type: EmptyCard }>();
const { t } = useI18n();

const { isMspRoute } = useIsMspRoute();
const tier = isMspRoute.value ? MSP : CUSTOMER;

const title = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security-solution",
      PHISHING_SIMULATION: "phishing-simulation-label",
      SAT: "sat-label",
      EMAIL_ARCHIVING: "email-archiving-label",
    })[props.type],
);

const icon = computed(
  () =>
    ({
      EMAIL_SECURITY: SERVICE_ICON.PHISH_SERVICE,
      PHISHING_SIMULATION: SERVICE_ICON.PHISHING_SIMULATION,
      SAT: SERVICE_ICON.SAT_SERVICE,
      EMAIL_ARCHIVING: SERVICE_ICON.ARC_SERVICE,
    })[props.type],
);

const text = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security",
      PHISHING_SIMULATION: "phishing-simulation-value",
      SAT: "sat-value",
      EMAIL_ARCHIVING: "email-archiving-value",
    })[props.type],
);

const documentationLink = computed(
  () =>
    ({
      EMAIL_SECURITY: EXTERNAL_DOCS_LINKS.EMAIL_SECURITY_MARKETING_PAGE,
      PHISHING_SIMULATION: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
      SAT: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
      EMAIL_ARCHIVING: EXTERNAL_DOCS_LINKS.ARC_TITAN_MARKETING_PAGE,
    })[props.type],
);

const tooltipText = computed(
  () =>
    ({
      EMAIL_SECURITY: t("email-security-solution"),
      PHISHING_SIMULATION: t("phishing-simulation-label"),
      SAT: t("sat-label"),
      EMAIL_ARCHIVING: t("email-archiving-label"),
    })[props.type],
);

const dataTestIdType = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security-solution",
      PHISHING_SIMULATION: "phishing-simulation-solution",
      SAT: "sat-solution",
      EMAIL_ARCHIVING: "email-archiving-solution",
    })[props.type],
);
</script>
